import type { MyBet } from '@ps/isp-web-services-types';
import { MultipleBetType } from '@ps/isp-web-services-types';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React from 'react';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import { Odds } from '@swf/sports/components';
import { getMultipleName } from '@swf/sports/utils/getMultipleName';

import {
  TXTWCL_1_event,
  TXTWCL_Bet_Builder,
  TXTWCL_EW,
  TXTWCL_N0_events,
  TXTWCL_N0_legs,
  TXTWCL_ReturnedX,
  TXTWCL_Single
} from './Header.messages.json';
import styles from './Header.scss';
import OpenBetsIcon from './components/OpenBetsIcon/OpenBetsIcon';
import SettledBetsIcon, { betResultMap } from './components/SettledBetsIcon/SettledBetsIcon';

export interface Props {
  bet: MyBet;
  returnValue: number;
}

const isMultipleBetType = (betType: string): betType is MultipleBetType =>
  Object.values(MultipleBetType).includes(betType as MultipleBetType);

const Header: React.FC<Props> = ({ bet, returnValue }) => {
  const { stakePerLine = 0 } = bet;
  const isSettledBet = bet.isSettled;
  const isSingleBet = bet.betType === 'SINGLE';
  const isBetBuilderBet = bet.isSgm;
  const isMultipleBet = !isSingleBet && !isBetBuilderBet;
  const singleBetPart = isSingleBet ? bet.legs[0].parts[0] : undefined;
  const isInPlay = bet.legs.some((leg) => leg.parts.some((part) => part.inPlay));
  const isEachWay = bet.legBetType === 'Eachway';
  const selectionName = isSingleBet ? bet.legs[0].parts[0].selectionName : undefined;
  const betBuilderData = isBetBuilderBet
    ? { numberOfEvents: bet.sgmEvents?.length, numberOfLegs: bet.legs.length }
    : undefined;
  const isVoidBet = bet.result === 'VOID';
  const isReturnedBet = bet.result === 'PLACED' || bet.result === 'SETTLED';

  return (
    <div className={styles.wrapper}>
      {isSettledBet && bet.result ? (
        <SettledBetsIcon result={bet.result} walletInfo={bet.walletInfo} />
      ) : (
        <OpenBetsIcon
          eventTypeId={Number(singleBetPart?.sportId)}
          isInPlay={isInPlay}
          isSgm={isBetBuilderBet}
          isMultiples={!isSingleBet}
          walletInfo={bet.walletInfo}
        />
      )}

      <div className={classNames(styles.text, { [styles.singleLine]: isMultipleBet })}>
        <div className={styles.title}>
          {isSettledBet && bet.result ? (
            <>
              {isReturnedBet ? (
                /**
                 * TODO: This condition will be removed in the post release clean ups
                 * once we have translation token TXTWCL_Returned_N0
                 */
                <>
                  <Translation {...TXTWCL_ReturnedX} />
                  {` `}
                  <CurrencyAmount currency={bet.currency} dollarValue value={returnValue} />
                </>
              ) : (
                <Translation
                  {...(betResultMap[bet.result]?.title || TXTWCL_ReturnedX)}
                  values={{
                    0: <CurrencyAmount currency={bet.currency} dollarValue value={returnValue} />
                  }}
                />
              )}
            </>
          ) : (
            <>
              <CurrencyAmount dollarValue value={stakePerLine} currency={bet.currency} />{' '}
              {bet.numLines > 1 && `x ${bet.numLines} `}
              {` `}-{` `}
              {!isMultipleBetType(bet.betType) ? (
                <Translation {...TXTWCL_Single} />
              ) : (
                getMultipleName({
                  betType: bet.betType,
                  numberOfLines: bet.numLines,
                  isEachWay,
                  isSgm: isBetBuilderBet
                })
              )}
              {bet.odds && (
                <>
                  {` `}@ <Odds {...bet.odds} />
                </>
              )}
              {isEachWay && (
                <>
                  {` `}
                  <Translation {...TXTWCL_EW} />
                </>
              )}
            </>
          )}
        </div>

        {isSingleBet && selectionName && (
          <div className={classNames(styles.subTitle, { [styles.voided]: isVoidBet })}>
            {selectionName}
          </div>
        )}

        {isBetBuilderBet && betBuilderData && (
          <div className={classNames(styles.subTitle, { [styles.voided]: isVoidBet })}>
            {isSettledBet && (
              <>
                <Translation {...TXTWCL_Bet_Builder} />
                {` `}
              </>
            )}
            {`(`}
            <Translation
              {...(betBuilderData.numberOfEvents === 1 ? TXTWCL_1_event : TXTWCL_N0_events)}
              values={{ 0: betBuilderData.numberOfEvents }}
            />
            {` - `}
            <Translation {...TXTWCL_N0_legs} values={{ 0: betBuilderData.numberOfLegs }} />
            {`)`}
          </div>
        )}

        {isMultipleBet && isSettledBet && (
          <div className={classNames(styles.subTitle, { [styles.voided]: isVoidBet })}>
            {isMultipleBetType(bet.betType) &&
              getMultipleName({
                betType: bet.betType,
                numberOfLines: bet.numLines,
                isEachWay
              })}
          </div>
        )}
      </div>
    </div>
  );
};
export default Header;
